// features/dexScreenerValidation/dexScreenerValidationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const DEX_CHAIN_RE =
  /^https?:\/\/(www\.)?dexscreener\.com\/(solana|ethereum|bsc|polygon|avalanche|fantom|arbitrum|optimism|base)\/[a-zA-Z0-9\-]+/i;

/**
 * @param {object} pair
 * @param {string} chain
 * @param {string} pathAddress
 * @param {'pair' | 'mint'} resolvedVia
 */
function buildValidationResult(pair, chain, pathAddress, resolvedVia) {
  return {
    isValid: true,
    tokenName: pair.baseToken?.name || "Unknown Token",
    tokenSymbol: pair.baseToken?.symbol || "UNKNOWN",
    price: pair.priceUsd ? parseFloat(pair.priceUsd).toFixed(6) : null,
    pairAddress: pair.pairAddress || (resolvedVia === "pair" ? pathAddress : null),
    mintAddress:
      pair.baseToken?.address ||
      (resolvedVia === "mint" ? pathAddress : null),
    chain: pair.chainId || chain,
    pairData: pair,
    resolvedVia,
    message:
      resolvedVia === "mint"
        ? "DexScreener mint URL validated successfully"
        : "DexScreener URL validated successfully",
  };
}

function pickBestPairForChain(pairs, chain) {
  if (!Array.isArray(pairs) || pairs.length === 0) return null;
  const sameChain = pairs.filter(
    (p) => String(p?.chainId || "").toLowerCase() === String(chain).toLowerCase(),
  );
  const pool = sameChain.length > 0 ? sameChain : pairs;
  return [...pool].sort(
    (a, b) => (Number(b?.liquidity?.usd) || 0) - (Number(a?.liquidity?.usd) || 0),
  )[0];
}

/**
 * Thunk: validateDexScreenerUrl
 * Accepts DexScreener pair OR mint (token) URLs:
 * - https://dexscreener.com/solana/<pairAddress>
 * - https://dexscreener.com/solana/<mintAddress>
 * @param {string} url
 */
export const validateDexScreenerUrl = createAsyncThunk(
  "dexScreenerValidation/validateUrl",
  async (url, { rejectWithValue }) => {
    try {
      if (!DEX_CHAIN_RE.test(url)) {
        throw new Error("Invalid DexScreener URL format");
      }

      const urlMatch = url.match(/dexscreener\.com\/([^\/]+)\/([^\/\?\#]+)/i);
      if (!urlMatch) {
        throw new Error("Could not extract pair information from URL");
      }

      const chain = urlMatch[1];
      const pathAddress = urlMatch[2];

      try {
        // 1) Treat path as pair address
        const pairRes = await fetch(
          `https://api.dexscreener.com/latest/dex/pairs/${chain}/${pathAddress}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          },
        );

        if (pairRes.ok) {
          const pairData = await pairRes.json();
          if (pairData?.pair?.pairAddress) {
            return buildValidationResult(pairData.pair, chain, pathAddress, "pair");
          }
        }

        // 2) Treat path as token mint — DexScreener token pages use /{chain}/{mint}
        const tokenRes = await fetch(
          `https://api.dexscreener.com/latest/dex/tokens/${pathAddress}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          },
        );

        if (!tokenRes.ok) {
          throw new Error(`DexScreener API error: ${tokenRes.status}`);
        }

        const tokenData = await tokenRes.json();
        const bestPair = pickBestPairForChain(tokenData?.pairs, chain);

        if (!bestPair?.pairAddress) {
          return {
            isValid: false,
            tokenName: null,
            tokenSymbol: null,
            price: null,
            pairAddress: null,
            mintAddress: pathAddress,
            chain,
            message: "Token or pair not found on DexScreener",
          };
        }

        return buildValidationResult(bestPair, chain, pathAddress, "mint");
      } catch (apiError) {
        return {
          isValid: false,
          tokenName: null,
          tokenSymbol: null,
          price: null,
          pairAddress: null,
          mintAddress: pathAddress,
          chain,
          message: apiError.message || "Failed to validate with DexScreener API",
        };
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

const initialState = {
  valid: null,
  loading: "idle",
  error: null,
  tokenInfo: null,
};

const dexScreenerValidationSlice = createSlice({
  name: "dexScreenerValidation",
  initialState,
  reducers: {
    resetValidation(state) {
      state.valid = null;
      state.error = null;
      state.loading = "idle";
      state.tokenInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateDexScreenerUrl.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.valid = null;
        state.tokenInfo = null;
      })
      .addCase(validateDexScreenerUrl.fulfilled, (state, action) => {
        state.loading = "idle";
        if (action.payload && typeof action.payload === "object") {
          state.valid = action.payload.isValid;
          state.tokenInfo = {
            name: action.payload.tokenName,
            symbol: action.payload.tokenSymbol,
            price: action.payload.price,
            pairAddress: action.payload.pairAddress,
            mintAddress: action.payload.mintAddress || null,
            chain: action.payload.chain,
            pairData: action.payload.pairData,
            resolvedVia: action.payload.resolvedVia || null,
            message: action.payload.message,
          };
        } else {
          state.valid = false;
          state.tokenInfo = null;
          state.error = "Invalid response format";
        }
        state.error = null;
      })
      .addCase(validateDexScreenerUrl.rejected, (state, action) => {
        state.loading = "idle";
        state.error = action.payload || action.error.message;
        state.valid = false;
        state.tokenInfo = null;
      });
  },
});

export const { resetValidation } = dexScreenerValidationSlice.actions;

export default dexScreenerValidationSlice.reducer;
